/**
 * ContactForm — public form rendered on /contact. Posts to /api/contact/messages.
 * The admin sees the submitted messages in /admin → Messages tab and can reply
 * from there (the reply is emailed to the visitor via SendGrid).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

export default function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit = name.trim() && email.trim() && body.trim() && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.submitContact({ name: name.trim(), email: email.trim(), subject: subject.trim(), body: body.trim() });
      setSent(true);
      trackEvent("contact_form_submitted");
      toast.success("Message sent — we'll get back to you within one business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="contact-form-sent"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={36} />
        <h3 className="font-heading text-xl font-bold text-emerald-900 mb-2">Message received</h3>
        <p className="text-sm text-emerald-800">
          Thanks {name.split(" ")[0] || "—"}, we received your message. We usually reply within one business day to <strong>{email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className="rounded-2xl border border-[var(--csd-border)] bg-white p-6 sm:p-8 space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact-name" className="block text-sm font-semibold mb-1.5">Your name *</Label>
          <Input
            id="contact-name"
            data-testid="contact-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            maxLength={200}
            required
          />
        </div>
        <div>
          <Label htmlFor="contact-email" className="block text-sm font-semibold mb-1.5">Your email *</Label>
          <Input
            id="contact-email"
            data-testid="contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            maxLength={200}
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="contact-subject" className="block text-sm font-semibold mb-1.5">Subject</Label>
        <Input
          id="contact-subject"
          data-testid="contact-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Refund request, question about Florida sale, etc."
          maxLength={200}
        />
      </div>

      <div>
        <Label htmlFor="contact-body" className="block text-sm font-semibold mb-1.5">Your message *</Label>
        <Textarea
          id="contact-body"
          data-testid="contact-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder="Tell us what you need — include your order ID or VIN if it's about a specific transaction."
          maxLength={5000}
          required
        />
        <div className="text-right text-xs text-[var(--csd-text-secondary)] mt-1 tabular-nums">{body.length}/5000</div>
      </div>

      <Button
        type="submit"
        disabled={!canSubmit}
        data-testid="contact-submit"
        className="h-11 rounded-xl bg-[var(--csd-primary)] hover:bg-[var(--csd-primary)]/90 text-white px-6"
      >
        {submitting ? (
          <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>
        ) : (
          <><Send size={16} className="mr-2" /> Send message</>
        )}
      </Button>

      <p className="text-xs text-[var(--csd-text-secondary)]">
        We use your email only to reply to this message. See our <a href="/privacy" className="text-[var(--csd-primary)] hover:underline">Privacy policy</a>.
      </p>
    </form>
  );
}
